import type { MeetingMinute } from '$lib/types/meeting';
import { MeetingPathSchema, transformToMeetingMinute } from '$lib/types/meeting';

// Simple in-memory cache for meeting data
const meetingCache = new Map<string, { data: MeetingMinute[]; timestamp: number }>();
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes in milliseconds

/**
 * Get cached meetings if still valid
 */
function getCachedMeetings(key: string): MeetingMinute[] | null {
	const cached = meetingCache.get(key);
	if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
		return cached.data;
	}
	return null;
}

/**
 * Set meetings in cache
 */
function setCachedMeetings(key: string, meetings: MeetingMinute[]): void {
	meetingCache.set(key, { data: meetings, timestamp: Date.now() });
}

/**
 * Safely extracts and validates path components from meeting file paths
 * Prevents path traversal and validates format
 */
function extractPathInfo(path: string) {
	// Security: Only allow specific pattern to prevent path traversal
	const pathMatch = path.match(/^\/src\/routes\/meetings\/(\d{4})\/([a-z0-9-]+)\/\+page\.svx$/);
	if (!pathMatch) return null;

	const [, year, slug] = pathMatch;

	// Validate using Zod schema
	const result = MeetingPathSchema.safeParse({ year, slug });
	if (!result.success) {
		console.warn(`Invalid path format: ${path}`, result.error.issues);
		return null;
	}

	return result.data;
}

/**
 * Load and validate meeting minutes from .svx files
 * Uses Zod for robust validation and type safety
 */
export async function loadMeetingMinutes(filterYear?: string): Promise<MeetingMinute[]> {
	const cacheKey = filterYear || 'all';

	// Check cache first
	const cached = getCachedMeetings(cacheKey);
	if (cached) {
		return cached;
	}

	try {
		// Import only metadata for efficiency
		const meetingModules = import.meta.glob('/src/routes/meetings/**/*.svx', {
			eager: true,
			import: 'metadata'
		});

		const meetings: MeetingMinute[] = [];

		for (const [path, metadataPromise] of Object.entries(meetingModules)) {
			try {
				// Skip template files
				if (path.includes('/templates/')) continue;

				// Extract and validate path info
				const pathInfo = extractPathInfo(path);
				if (!pathInfo) continue;

				// Filter by year early for efficiency
				if (filterYear && pathInfo.year !== filterYear) continue;

				// Resolve metadata safely
				const metadata = await Promise.resolve(metadataPromise);

				// Transform and validate using Zod
				const meetingMinute = transformToMeetingMinute(metadata, pathInfo);
				if (meetingMinute) {
					meetings.push(meetingMinute);
				}
			} catch (error) {
				console.warn(`Error processing meeting file ${path}:`, error);
				// Continue processing other files
				continue;
			}
		}

		// Cache the results
		setCachedMeetings(cacheKey, meetings);

		return meetings;
	} catch (error) {
		console.error('Error loading meeting minutes:', error);
		return []; // Return empty array on failure
	}
}

/**
 * Load only published meeting minutes, sorted by date (newest first)
 * Optimized for public-facing pages
 */
export async function loadPublishedMeetingMinutes(filterYear?: string): Promise<MeetingMinute[]> {
	const allMeetings = await loadMeetingMinutes(filterYear);
	return allMeetings
		.filter((meeting) => meeting.published === true)
		.sort((a, b) => b.date.getTime() - a.date.getTime());
}

/**
 * Group meetings by year for yearly navigation
 * Maintains sort order within each year
 */
export function groupMeetingsByYear(meetings: MeetingMinute[]): Record<string, MeetingMinute[]> {
	const grouped: Record<string, MeetingMinute[]> = {};

	for (const meeting of meetings) {
		const year = meeting.date.getFullYear().toString();
		if (!grouped[year]) {
			grouped[year] = [];
		}
		grouped[year].push(meeting);
	}

	// Sort meetings within each year (newest first)
	Object.values(grouped).forEach((yearMeetings) => {
		yearMeetings.sort((a, b) => b.date.getTime() - a.date.getTime());
	});

	return grouped;
}

/**
 * Get recent published minutes (for homepage/dashboard)
 * Limits results for performance
 */
export async function getRecentPublishedMinutes(limit = 5): Promise<MeetingMinute[]> {
	const published = await loadPublishedMeetingMinutes();
	return published.slice(0, limit);
}

/**
 * Check if a meeting exists and is published
 * Useful for conditional rendering
 */
export async function isMeetingPublished(year: string, slug: string): Promise<boolean> {
	const meetings = await loadMeetingMinutes(year);
	const meeting = meetings.find((m) => m.slug === `/meetings/${year}/${slug}`);
	return meeting?.published === true;
}

/**
 * Get meeting statistics for dashboard/admin views
 */
export async function getMeetingStats(): Promise<{
	total: number;
	published: number;
	draft: number;
	byYear: Record<string, number>;
	byType: Record<string, number>;
}> {
	const allMeetings = await loadMeetingMinutes();
	const published = allMeetings.filter((m) => m.published === true);

	const byYear: Record<string, number> = {};
	const byType: Record<string, number> = {};

	for (const meeting of allMeetings) {
		const year = meeting.date.getFullYear().toString();
		byYear[year] = (byYear[year] || 0) + 1;
		byType[meeting.type] = (byType[meeting.type] || 0) + 1;
	}

	return {
		total: allMeetings.length,
		published: published.length,
		draft: allMeetings.length - published.length,
		byYear,
		byType
	};
}

/**
 * Find meetings by search term (title or summary)
 */
export async function searchMeetings(
	searchTerm: string,
	publishedOnly = true
): Promise<MeetingMinute[]> {
	const meetings = publishedOnly ? await loadPublishedMeetingMinutes() : await loadMeetingMinutes();

	const term = searchTerm.toLowerCase();
	return meetings.filter(
		(meeting) =>
			meeting.title.toLowerCase().includes(term) || meeting.summary?.toLowerCase().includes(term)
	);
}

/**
 * Get meetings by type and optional year
 */
export async function getMeetingsByType(
	type: 'General Meeting' | 'Board Meeting' | 'Special Meeting',
	year?: string,
	publishedOnly = true
): Promise<MeetingMinute[]> {
	const meetings = publishedOnly
		? await loadPublishedMeetingMinutes(year)
		: await loadMeetingMinutes(year);

	return meetings.filter((meeting) => meeting.type === type);
}

/**
 * Clear meeting cache - useful for development or admin functions
 */
export function clearMeetingCache(): void {
	meetingCache.clear();
}

/**
 * Get cache status for debugging
 */
export function getCacheStatus(): { size: number; keys: string[] } {
	return {
		size: meetingCache.size,
		keys: Array.from(meetingCache.keys())
	};
}
